// Device capability probe — run ONCE before the upload batch to decide whether
// on-device scanning is viable. If not, the uploader skips it and uploads plain
// (prescanned:false), so the backend scans server-side exactly as today. The
// whole feature is opt-in-per-device and fails safe.
//
// probeScanSupport() actually loads a model and runs one warm-up inference
// within a time budget (a device may "support" WASM but be far too slow to be
// worth it at batch scale). Pure decision logic is split out so it's testable.

const DEFAULT_BUDGET = {
  timeoutMs: 10000, // give up probing after this
  maxWarmupMs: 1500, // slower than this per 640² inference => not worth it at scale
};

// Pure verdict from probe measurements — unit-testable without a browser/model.
export function evaluateProbe({ hasWasm, hasBitmap, warmupMs, error }, budget = DEFAULT_BUDGET) {
  if (error) return { ok: false, reason: `probe failed: ${error}` };
  if (!hasWasm) return { ok: false, reason: 'no WebAssembly' };
  if (!hasBitmap) return { ok: false, reason: 'no createImageBitmap' };
  if (warmupMs == null) return { ok: false, reason: 'no warm-up timing' };
  if (warmupMs > budget.maxWarmupMs) {
    return { ok: false, reason: `too slow (${Math.round(warmupMs)}ms/inference > ${budget.maxWarmupMs}ms budget)` };
  }
  return { ok: true, warmupMs };
}

const withTimeout = (p, ms, label) => Promise.race([
  p,
  new Promise((_, rej) => setTimeout(() => rej(new Error(`${label} timed out after ${ms}ms`)), ms)),
]);

// Measure one backend: build a session on the given execution providers and time a
// steady-state inference. Returns { verdict, warmupMs? } — verdict is from evaluateProbe.
async function measureBackend(ort, faceModelUrl, eps, budget, label) {
  const hasWasm = typeof WebAssembly !== 'undefined';
  const hasBitmap = typeof createImageBitmap !== 'undefined';
  const session = await withTimeout(
    ort.InferenceSession.create(faceModelUrl, { executionProviders: eps, graphOptimizationLevel: 'all' }),
    budget.timeoutMs, `model load (${label})`,
  );
  const inputName = session.inputNames[0];
  const size = 640;
  const warm = new ort.Tensor('float32', new Float32Array(3 * size * size), [1, 3, size, size]);
  // Run twice and measure the SECOND: the first inference pays a one-time cost
  // (WebGPU shader compilation / WASM graph warm-up) that isn't representative of
  // steady-state batch throughput.
  await withTimeout(session.run({ [inputName]: warm }), budget.timeoutMs, `warm-up (${label}, compile)`);
  const t0 = performance.now();
  await withTimeout(session.run({ [inputName]: warm }), budget.timeoutMs, `warm-up (${label})`);
  const warmupMs = performance.now() - t0;
  try { session.release?.(); } catch { /* best-effort */ }
  return { verdict: evaluateProbe({ hasWasm, hasBitmap, warmupMs }, budget), warmupMs };
}

// Browser-only. `ort` is the onnxruntime-web module; `faceModelUrl` a reachable .onnx.
// `useWebGpu` (from scanConfig) says whether to ATTEMPT WebGPU. The decision is made
// ONCE here, up front, for the whole batch: try WebGPU first when asked, and if it
// won't build OR is too slow, fall back and measure WASM — whichever backend wins is
// the one the worker pool then runs. Returns { ok, reason?, warmupMs?, backend }.
export async function probeScanSupport(ort, { faceModelUrl, useWebGpu = false, budget = DEFAULT_BUDGET } = {}) {
  const hasWasm = typeof WebAssembly !== 'undefined';
  const hasBitmap = typeof createImageBitmap !== 'undefined';
  const wantWebGpu = !!useWebGpu && typeof navigator !== 'undefined' && !!navigator.gpu;

  if (!hasWasm || !hasBitmap || !faceModelUrl) {
    return { ...evaluateProbe({ hasWasm, hasBitmap, warmupMs: null }, budget), backend: 'wasm' };
  }

  // Candidate backends, best-first. WebGPU falls back to WASM per-op internally, but
  // we still list WASM as its own candidate so a WebGPU build-failure OR a too-slow
  // GPU cleanly drops to a WASM measurement instead of disabling scanning outright.
  const candidates = wantWebGpu
    ? [{ backend: 'webgpu', eps: ['webgpu', 'wasm'] }, { backend: 'wasm', eps: ['wasm'] }]
    : [{ backend: 'wasm', eps: ['wasm'] }];

  let last = { ...evaluateProbe({ hasWasm, hasBitmap, warmupMs: null }, budget), backend: 'wasm' };
  for (const c of candidates) {
    try {
      const { verdict, warmupMs } = await measureBackend(ort, faceModelUrl, c.eps, budget, c.backend);
      if (verdict.ok) return { ...verdict, backend: c.backend };
      // Built but too slow → remember and try the next (e.g. WASM) candidate.
      last = { ...verdict, backend: c.backend };
    } catch (e) {
      // Failed to build on this backend → try the next candidate.
      last = { ...evaluateProbe({ hasWasm, hasBitmap, warmupMs: null, error: String((e && e.message) || e) }, budget), backend: c.backend };
    }
  }
  return last;
}
